import { FC, useEffect, useRef, ReactElement, cloneElement, isValidElement, MouseEvent } from 'react';
import { debounce } from 'throttle-debounce';
import { addEvent, checkWrappedBy } from 'ava-util/dist/dom-event';
import { isInViewport, isVisible } from 'ava-util/dist/dom-node';
import { ITrackVariant } from 'base/interfaces/product';
import {
    ITrackElementData,
    getImpressedKey,
    trackElement,
    trackProductClick,
    trackProductsImpression,
    isProduct,
    isElement,
    displayCtr,
} from 'base/utils/tracker';
import { ISpElementData } from 'base/utils/tracker-sp';
import { filterFields } from 'ava-util/dist/data';
import { IProduct } from 'mod/product/interface';

export type ITrackerData =
    ITrackElementData
    | ITrackVariant
    | (ITrackElementData & ITrackVariant)
    | IProduct & { position?: number | string }
    | (ITrackElementData & IProduct);

const imprList: Record<string, {
    node: HTMLElement;
    data: ITrackerData;
}> = {}; // 存放所有节点数据
const INTERVAL = 200; //imp 打点延迟时间间隔

function filterProductFields(data: ITrackerData): ITrackVariant {
    const filtered = filterFields(data, [
        'variantId',
        'trackId',
        'productTrackCode',
        'variantTrackCode',
        'categoryTrackCode',
        'dealPrice',
        'shopPrice',
        'listTrackCode',
        'num',
        'position',
        'track',
    ]) as ITrackVariant;

    if (!filtered.listTrackCode) {
        filtered.listTrackCode = 'module' in data ? data.module : '';
    }

    return filtered;
}

function filterElementFields(data: ITrackerData | IProduct): ITrackElementData {
    return filterFields(data, [
        'action',
        'targetType',
        'target',
        'relTarget',
        'module',
        'position',
        'creative',
        'content',
        'value',
    ]) as ITrackElementData;
}

/**
 * imprList 中新增节点，返回新增加点的 索引
 * @param node Dom 元素
 * @param data snowplow data 数据
 */
function addImprList(node: HTMLElement, data: ITrackerData): string {
    const key = getImpressedKey(data);
    if (!imprList[key]) {
        imprList[key] = { node, data };
    }
    return key;
}

/**
 * imprList 中移除节点
 * @param key 节点在 imprList 中的索引
 */
function removeImprList(key: string): void {
    delete imprList[key];
}

/**
 * 判断 Dom 元素是否可以执行 trackImpression
 * @param node Dom 元素
 */
function canTrackImp(node: HTMLElement): boolean {
    return node && isInViewport(node) && (isVisible(node) || !!node.getAttribute('data-aos'))
}

/**
 * 窗口滚动缩放时检查所有尚未执行 impression 的节点
 */
export const autoTrackImp = debounce(INTERVAL, () => {
    const products: ITrackVariant[] = [];
    const elements: ITrackElementData[] = [];
    Object.entries(imprList).forEach(([key, { node, data }]) => {
        if (canTrackImp(node)) {
            if (isElement(data)) {
                elements.push(filterElementFields(data));
            }
            if (isProduct(data)) {
                products.push(filterProductFields(data));
            }
            removeImprList(key);
        }
    });
    if (products.length > 0) {
        trackProductsImpression(products);
    }
    if (elements.length > 0) {
        trackElement(elements);
    }
});

export type TTrackerTrigger = 'impr' | 'click' | 'impr click' | 'click impr';

export type ITrackerProps = {
    trigger?: TTrackerTrigger;
    data?: ITrackerData;
    onClick?: () => void;
    disableProdAttributes?: boolean;
    async?: boolean;
    children?: React.ReactNode;
};

const Tracker: FC<ITrackerProps> = props => {
    const {
        trigger = 'impr click',
        data,
        onClick,
        disableProdAttributes = false,
        async = false,
        children
    } = props;

    const childRef = useRef<HTMLElement>(null);
    const initialized = useRef<boolean>(false);
    const isProd = data ? isProduct(data) : false;
    const isElem = data ? isElement(data) : false;

    let cloneProps = { ref: childRef };
    if (data) {
        if (isElem) {
            cloneProps['data-t-tar'] = String((data as ISpElementData).target || '');
            cloneProps['data-t-pos'] = String(data.position || '');
        }
        if (isProd && !disableProdAttributes) {
            cloneProps['data-t-vid'] = (data as ITrackVariant).variantId || void 0;
            cloneProps['data-t-price'] = 'track' in data
                ? data.shopPrice
                : Number((data as ITrackVariant).dealPrice) || (data as ITrackVariant).shopPrice;
        }
    }

    const handleClick = (e: MouseEvent) => {
        const node = childRef.current;
        if (!node || !data) {
            return;
        }
        const links = node.querySelectorAll('.js-track-link');
        if (links.length > 0) {
            let isLinkClicked = false;
            for (let i = 0; i < links.length; i++) {
                if (checkWrappedBy(e, links[i])) {
                    isLinkClicked = true;
                    break;
                }
            }
            if (!isLinkClicked) {
                return;
            }
        }

        if (isElem) {
            trackElement({ ...filterElementFields(data), action: 'click' } as ITrackElementData)
        }
        if (isProd) {
            trackProductClick(filterProductFields(data));
        }
        onClick?.();
    }

    useEffect(() => {
        const node = childRef?.current;
        if (!node || !data || initialized.current) {
            return;
        }

        initialized.current = true;
        const hasImpr = trigger.includes('impr');
        const hasClick = trigger.includes('click');
        let key: string;
        let ob: IntersectionObserver;

        if (hasClick) {
            addEvent(node, 'click', handleClick);
        }

        if (hasImpr) {
            key = addImprList(node, { ...data, action: 'impr' } as ITrackerData);
            ob = new IntersectionObserver(entries => {
                    for (let entry of entries) {
                        if (entry.isIntersecting) {
                            autoTrackImp();
                            ob.unobserve(entry.target);
                        }
                    }
                }, {
                    rootMargin: '0px 0px 0px 0px'
                }
            );
            ob.observe(node as HTMLElement);
        }

        if (async && 'module' in data) {
            displayCtr(data.module as string);
        }

        return () => {
            ob?.disconnect();
            if (key) {
                removeImprList(key);
            }
        }
    }, [data]);

    useEffect(() => {
        return () => {
            if (!!data && initialized.current) {
                const key = getImpressedKey(data);
                const hasImpr = trigger.includes('impr');
                key && hasImpr && removeImprList(key);
            }
        }
    }, []);

    let child: ReactElement;
    if (!data) {
        child = typeof children === 'function' ? children() : children;
        return isValidElement(child) ? child : null;
    }
    child = typeof children === 'function' ? children({ trackClick: handleClick }) : children;

    return isValidElement(child) ? cloneElement((child as ReactElement), cloneProps) : null;
}

export default Tracker;